import api, { handleResponse, handleError } from './api';

/**
 * Vendor pages inside Pros — menu, vendor offers (claim / redeem), venue events, analytics and
 * the menu importer. Booking offers stay in proService; these are the things a place posts.
 */
const wrap = async (call) => { try { return handleResponse(await call()); } catch (e) { return handleError(e); } };
/** Same envelope plus the HTTP `status` on failure — the page-events and tabs blocks hide themselves on a 404 while the API lands. */
const wrapWithStatus = async (call) => { try { return handleResponse(await call()); } catch (e) { return { ...handleError(e), status: e?.response?.status ?? null }; } };

const vendorService = {
  /** Static reference: kinds, per-kind attribute sets, dietary marks, layouts, importer availability. */
  kinds: () => wrap(() => api.get('/pro/kinds')),

  // ── Menu ──
  /** Replace the whole menu. { menuLayout, importUrl?, sections: [{ id?, name, note, layout, photoPath, showAbv, items: [...] }] } */
  saveMenu: (payload) => wrap(() => api.put('/pro/menu', payload)),
  /** PASTE A MENU → proposal. Nothing is saved until saveMenu. */
  parseMenuText: (text) => wrap(() => api.post('/pro/menu/parse-text', { text })),
  /** IMPORT FROM WEBSITE → proposal. Nothing is saved until saveMenu. */
  importMenuUrl: (url, readAnyway = false) => wrap(() => api.post('/pro/menu/import-url', { url, readAnyway })),

  // ── Venue events (legacy: the wizard's host-only candidate list) ──
  venueEventCandidates: () => wrap(() => api.get('/pro/venue-events/candidates')),

  // ── Page events (every kind) — "events on this page", managed from the dashboard ──
  /** Every event linked to the page, any status, in page order. `status` is set on failure so a 404 (endpoint not there yet) can hide the block. */
  pageEvents: (pageId) => wrapWithStatus(() => api.get(`/pro/pages/${pageId}/events`)),
  /** Link any linkable event (public, or one the caller hosts). Idempotent. Answers with the PageEventDto. */
  linkPageEvent: (pageId, eventId) => wrapWithStatus(() => api.post(`/pro/pages/${pageId}/events`, { eventId })),
  unlinkPageEvent: (pageId, eventId) => wrapWithStatus(() => api.delete(`/pro/pages/${pageId}/events/${eventId}`)),
  /** Empty `q` → the caller's own unlinked events; otherwise theirs first, then the public catalogue. Rows carry `isMine` + `isLinked`. */
  suggestPageEvents: (pageId, q = '', take = 20) => wrapWithStatus(() => api.get(`/pro/pages/${pageId}/events/suggest`, { params: { q: q || '', take } })),

  // ── Tab visibility ──
  /** Which public tabs are hidden. `about` can never be hidden; unknown keys are dropped server-side. */
  setHiddenTabs: (pageId, hiddenTabs) => wrapWithStatus(() => api.put(`/pro/pages/${pageId}/tabs`, { hiddenTabs: hiddenTabs || [] })),
  /** Hidden tabs, their order and the default tab — send only the parts that changed. */
  setTabs: (pageId, parts) => wrapWithStatus(() => api.put(`/pro/pages/${pageId}/tabs`, parts || {})),
  /** The embed SDK's feed, as the script on someone's site reads it (public). */
  embedEvents: (slug, opts = {}) => wrap(() => api.get('/embed/events', { params: { page: slug, ...opts } })),

  // ── Vendor offers ──
  offersForPage: (slug) => wrap(() => api.get(`/pro/${slug}/vendor-offers`)),
  myOffers: () => wrap(() => api.get('/pro/vendor-offers')),
  createOffer: (input) => wrap(() => api.post('/pro/vendor-offers', input)),
  updateOffer: (id, input) => wrap(() => api.put(`/pro/vendor-offers/${id}`, input)),
  deleteOffer: (id) => wrap(() => api.delete(`/pro/vendor-offers/${id}`)),
  claimOffer: (slug, id) => wrap(() => api.post(`/pro/${slug}/vendor-offers/${id}/claim`)),
  /** { claimId } | { code } | { raw }. markUsed=false just looks it up. Always answers with a verdict. */
  redeem: (payload, markUsed = true) => wrap(() => api.post(`/pro/vendor-offers/redeem?markUsed=${markUsed ? 'true' : 'false'}`, payload)),

  // ── Guest side ──
  myClaims: () => wrap(() => api.get('/vendor-offers/mine')),
  getClaim: (claimId) => wrap(() => api.get(`/vendor-offers/claims/${claimId}`)),
  releaseClaim: (claimId) => wrap(() => api.delete(`/vendor-offers/claims/${claimId}`)),

  // ── Analytics ──
  track: (slug, events) => wrap(() => api.post(`/pro/${slug}/track`, { events })),
  analytics: (days = 30) => wrap(() => api.get('/pro/analytics', { params: { days, tzOffset: new Date().getTimezoneOffset() } })),
};

export default vendorService;
